import React, { useEffect, useRef, useState } from 'react';
import { Button, Stack, Tooltip } from '@mui/material';
import {
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import { useQueryClient } from '@tanstack/react-query';
import {
  ImportControlModeTemplateErrorDto,
  ImportControlModeTemplateResponseDto,
} from '@src/types/ControlModes';
import ImportErrorDialog from '../Modes/components/ImportErrorDialog';

export interface FileToolbarProps {
  handleAdd: () => void;
  importFile: (
    file: File,
  ) => Promise<ImportControlModeTemplateResponseDto | null>;
  exportFile: () => Promise<boolean>;
  queryKey: string[];
  entityName: string;
  validation: {
    validateTemplateFile: (file: File) => Promise<boolean>;
    isValidating: boolean;
    error: ImportControlModeTemplateErrorDto | Error | null;
    resetError: () => void;
  };
  importHook: {
    isImporting: boolean;
    error: ImportControlModeTemplateErrorDto | Error | null;
  };
  exportHook: {
    isExporting: boolean;
  };
  onImportSuccess?: (result: ImportControlModeTemplateResponseDto) => void;
}

export default function FileToolbar({
  handleAdd,
  importFile,
  exportFile,
  queryKey,
  entityName,
  validation,
  importHook,
  exportHook,
  onImportSuccess,
}: FileToolbarProps): React.ReactElement {
  const {
    validateTemplateFile,
    isValidating,
    error: validationError,
    resetError: resetValidationError,
  } = validation;

  const { isImporting, error: importApiError } = importHook;

  const { isExporting } = exportHook;

  const fileInputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [genericError, setGenericError] = useState<Error | null>(null);

  const handleExportClick = async () => {
    await exportFile();
  };

  const handleImportClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleError = (error: unknown) => {
    setErrorDialogOpen(true);

    if (!(error && typeof error === 'object' && 'statusCode' in error)) {
      const newGenericError =
        error instanceof Error
          ? error
          : new Error(
              `Failed to import ${entityName.toLowerCase()}. Please try again.`,
            );
      setGenericError(newGenericError);
      resetValidationError();
    }
  };

  const handleFileChange = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    try {
      resetValidationError();

      const isValid = await validateTemplateFile(file);

      if (!isValid) {
        setErrorDialogOpen(true);
        if (event.target) {
          event.target.value = '';
        }
        return;
      }

      importFile(file)
        .then((result) => {
          if (result) {
            queryClient.invalidateQueries({
              queryKey: queryKey,
            });

            if (onImportSuccess) {
              onImportSuccess(result);
            }
          }
        })
        .catch(handleError);
    } catch (error) {
      handleError(error);
    }

    // Reset the file input
    if (event.target) {
      event.target.value = '';
    }
  };

  const handleErrorDialogClose = () => {
    setErrorDialogOpen(false);
    setGenericError(null);
  };

  useEffect(() => {
    if (importApiError) {
      setErrorDialogOpen(true);
    }
  }, [importApiError]);

  return (
    <>
      <GridToolbarContainer>
        <Stack
          direction='row'
          spacing={2}
          sx={{ flexGrow: 1, p: 1 }}
          justifyContent={'space-between'}
        >
          <div>
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={handleAdd}
            >
              Add {entityName}
            </Button>
          </div>

          <Stack spacing={2} direction='row'>
            <GridToolbarFilterButton />

            <Tooltip title={`Export ${entityName.toLowerCase()} as JSON file`}>
              <Button
                variant='outlined'
                startIcon={<FileDownloadIcon />}
                onClick={handleExportClick}
                disabled={isExporting}
              >
                Export
              </Button>
            </Tooltip>

            <Tooltip
              title={`Import ${entityName.toLowerCase()} from JSON file`}
            >
              <Button
                variant='outlined'
                startIcon={<FileUploadIcon />}
                onClick={handleImportClick}
                disabled={isImporting || isValidating}
              >
                Import
              </Button>
            </Tooltip>

            <input
              type='file'
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept='.json'
              onChange={handleFileChange}
            />

            <GridToolbarQuickFilter />
          </Stack>
        </Stack>
      </GridToolbarContainer>

      <ImportErrorDialog
        open={errorDialogOpen}
        onClose={handleErrorDialogClose}
        error={validationError || importApiError || genericError}
      />
    </>
  );
}
